/*
 * Content data mirrored from /app/backend/data_content.py so the frontend
 * can render service, location and blog pages without a round-trip.
 * Server endpoints are still available for form submission and dynamic
 * scenarios; this static mirror keeps SEO copy identical for indexing.
 */

export const SERVICES = [
  {
    slug: "home-care-for-seniors",
    title: "Home Care for Seniors",
    tagline: "Independence, preserved. Dignity, protected.",
    division: "home-care",
    cover: "https://images.unsplash.com/photo-1765896387387-0538bc9f997e",
    meta_description:
      "Compassionate senior home care across Canada. Personal support, meal prep, medication reminders and companionship delivered by vetted, bonded PSWs from Xtreme Care Services.",
    keywords: ["senior home care Canada", "elderly in-home assistance", "aging in place services", "PSW for seniors"],
    intro:
      "Aging in place is not simply a preference — for most Canadian seniors it is a promise they made to themselves decades ago. Our home care program honours that promise. We build a stable rotation of two to three PSWs around each client so a familiar face arrives every shift, without exception.",
    benefits: [
      { title: "A rotation, not a stranger", body: "Two to three PSWs assigned per household so continuity is the rule, not the exception." },
      { title: "Care plans, not checklists", body: "Each plan is built with the family and a registered nurse before day one." },
      { title: "Bonded, insured, vetted", body: "Every caregiver clears a vulnerable-sector background check and provincial credentials." },
    ],
    how_it_works: [
      "Free 30-minute in-home or video consultation.",
      "A registered nurse authors a personalised care plan.",
      "Meet your caregiver rotation before the first shift.",
      "Ongoing supervision and monthly plan reviews.",
    ],
    faq: [
      { q: "What are the minimum visit hours?", a: "Two hours per visit is our minimum in most provinces, with overnight and 24-hour options available." },
      { q: "Do you cover rural areas?", a: "Yes. We staff clients across every province and territory in Canada, including remote and rural households." },
      { q: "Is care covered by government funding?", a: "Certain provinces reimburse portions through home and community care programs — we help families navigate the paperwork." },
    ],
    cta: "Request an in-home consultation",
  },
  {
    slug: "dementia-care",
    title: "Dementia & Memory Care",
    tagline: "Familiar routines. Trained hands. Calmer days.",
    division: "home-care",
    cover: "https://images.pexels.com/photos/5790833/pexels-photo-5790833.jpeg",
    meta_description:
      "Specialised dementia and Alzheimer's care at home. Xtreme Care Services trains every PSW in redirection, sundowning management and validation therapy — nationwide.",
    keywords: ["dementia care at home", "Alzheimer's support caregivers", "memory care assistance Canada"],
    intro:
      "Dementia does not shrink a person — it rearranges their world. Our memory care model is built around routines that feel safe, cues that feel familiar, and PSWs who have completed dedicated Gentle Persuasive Approach training.",
    benefits: [
      { title: "Sundowning protocols", body: "Structured evening routines that reduce agitation without over-medication." },
      { title: "Validation, not correction", body: "Our caregivers meet clients where they are, in the era their memory has settled into." },
      { title: "Family coaching", body: "We teach spouses and adult children the language that keeps their loved one calm." },
    ],
    how_it_works: [
      "Cognitive assessment with an RN.",
      "Environmental audit of the home (lighting, contrast, wayfinding).",
      "Match with a memory-care-certified PSW rotation.",
      "Quarterly re-assessments as needs evolve.",
    ],
    faq: [
      { q: "Can you handle wandering behaviours?", a: "Yes — we deploy line-of-sight supervision and can install passive door sensors." },
      { q: "What if my parent refuses care at first?", a: "Our intake includes a soft-start visit where the PSW simply visits without providing hands-on care." },
    ],
    cta: "Speak with a memory care specialist",
  },
  {
    slug: "palliative-care-support",
    title: "Palliative Care Support",
    tagline: "Comfort. Presence. Grace at the threshold.",
    division: "home-care",
    cover: "https://images.pexels.com/photos/6975192/pexels-photo-6975192.jpeg",
    meta_description:
      "Compassionate palliative and end-of-life home care in Canada. Xtreme Care Services partners with palliative physicians to provide comfort-focused support around the clock.",
    keywords: ["palliative care support Canada", "end of life in-home care", "comfort care services"],
    intro:
      "When the medical goal shifts from cure to comfort, the work becomes sacred. We stand beside families through this passage — coordinating with palliative physicians, keeping pain managed, and holding vigil so no one is alone.",
    benefits: [
      { title: "24/7 continuous presence", body: "Overnight and around-the-clock coverage during the final weeks." },
      { title: "Symptom vigilance", body: "PSWs document pain scales, respiration and comfort markers for the palliative team." },
      { title: "Bereavement handoff", body: "We remain available to the family for two weeks after passing." },
    ],
    how_it_works: [
      "Introductory call with our palliative coordinator.",
      "Coordination with your existing palliative physician.",
      "24/7 rotation deployed within 48 hours.",
      "Post-passing family debrief.",
    ],
    faq: [
      { q: "Do you administer medications?", a: "PSWs assist and remind; licensed nurses administer where required by provincial scope." },
      { q: "Will the same caregiver stay through the final days?", a: "We honour continuity — the primary PSW is prioritised for the family's chosen days." },
    ],
    cta: "Arrange palliative support",
  },
  {
    slug: "disability-support-services",
    title: "Disability Support Services",
    tagline: "Autonomy, amplified by the right support.",
    division: "home-care",
    cover: "https://images.pexels.com/photos/5430213/pexels-photo-5430213.jpeg",
    meta_description:
      "Adult and youth disability support services across Canada. Xtreme Care Services supplies developmental service workers and PSWs for community participation, personal care and life skills.",
    keywords: ["disability support workers", "special needs caregivers Canada", "independent living assistance"],
    intro:
      "Support that follows the person — not the paperwork. We work with individuals living with physical, developmental and acquired disabilities to build weekly plans that centre what the person actually wants to do this week.",
    benefits: [
      { title: "Person-directed planning", body: "Goals authored by the client, not imposed from a template." },
      { title: "Community access", body: "Support to attend classes, work, appointments and social events." },
      { title: "Behavioural training", body: "DSWs trained in CPI non-violent crisis intervention." },
    ],
    how_it_works: [
      "Discovery session with the individual and family.",
      "Match with a developmental service worker.",
      "Weekly plans reviewed against personal goals.",
      "Quarterly outcomes report.",
    ],
    faq: [
      { q: "Do you accept funding from Passport, AISH, or provincial equivalents?", a: "Yes — our billing team invoices directly against provincial disability funding programs." },
    ],
    cta: "Design a support plan",
  },
  {
    slug: "hospital-discharge-support",
    title: "Hospital Discharge Support",
    tagline: "The first 72 hours home, handled.",
    division: "home-care",
    cover: "https://images.unsplash.com/photo-1584451049700-ec9b394f3805",
    meta_description:
      "Post-hospital transition care in Canada. Xtreme Care Services deploys PSWs and RNs within 24 hours of discharge to prevent readmission and support recovery.",
    keywords: ["hospital to home care", "post-surgery recovery assistance", "discharge transition caregivers"],
    intro:
      "The 72 hours after discharge are the most fragile of any recovery. Prescriptions are new, wounds are fresh, energy is bottomed out, and one skipped instruction can send someone right back to emergency. We prevent that.",
    benefits: [
      { title: "24-hour deployment", body: "PSWs and RNs available on the ward before you leave." },
      { title: "Medication reconciliation", body: "A nurse compares pre-admission and post-discharge lists to catch conflicts." },
      { title: "Follow-up choreography", body: "We book, drive to, and document every follow-up appointment." },
    ],
    how_it_works: [
      "Ward-side intake with the discharge planner.",
      "Home safety audit before the client returns.",
      "Continuous presence for the first 72 hours.",
      "Stepped-down care thereafter.",
    ],
    faq: [
      { q: "How quickly can you deploy?", a: "In most Canadian cities we can begin service within 24 hours of the referral call." },
    ],
    cta: "Prepare a safe discharge",
  },
  {
    slug: "live-in-caregivers",
    title: "Live-In Caregivers",
    tagline: "One trusted presence, twenty-four hours a day.",
    division: "home-care",
    cover: "https://images.pexels.com/photos/6129496/pexels-photo-6129496.jpeg",
    meta_description:
      "Live-in caregivers and 24/7 PSW care across Canada from Xtreme Care Services. Ideal for advanced needs, cognitive decline and families needing continuous presence.",
    keywords: ["24/7 live-in caregivers Canada", "full-time home care", "live-in PSW services"],
    intro:
      "For clients who cannot be safely alone, a rotating live-in model offers the continuity of a single relationship without the burnout of a single caregiver. We staff two live-in PSWs on alternating weeks so trust deepens while care never falters.",
    benefits: [
      { title: "Two-person rotation", body: "Consistent faces on alternating weeks." },
      { title: "Whole-day rhythms", body: "Meals, hygiene, medication and companionship woven into one flow." },
      { title: "Overnight vigilance", body: "Awake or on-call overnight depending on needs." },
    ],
    how_it_works: [
      "In-home assessment for suitable accommodation.",
      "Live-in PSW pair selected and introduced.",
      "Two-week supervised onboarding.",
      "Monthly RN visit thereafter.",
    ],
    faq: [
      { q: "What accommodations do you require?", a: "A private room with a door and access to a bathroom is the standard requirement." },
    ],
    cta: "Arrange live-in coverage",
  },
  {
    slug: "staffing-for-long-term-care-homes",
    title: "Staffing for Long-Term Care Homes",
    tagline: "Fill the gap without breaking the standard.",
    division: "staffing",
    cover: "https://images.unsplash.com/photo-1516841273335-e39b37888115",
    meta_description:
      "Xtreme Care Services supplies pre-credentialed PSWs and RPNs to Canadian long-term care homes on short-, medium- and long-term contracts.",
    keywords: ["LTC home staffing agency", "long term care PSWs Canada", "healthcare facility staffing"],
    intro:
      "Long-term care leaders know: a single missed shift ripples across an entire wing. We keep a rostered bench of pre-credentialed PSWs and RPNs ready to walk in fully oriented — nights, weekends, statutory holidays, no exceptions.",
    benefits: [
      { title: "Same-shift fill rate above 96%", body: "Because our roster is over-supplied against seasonal demand." },
      { title: "Facility-specific orientation", body: "Our staff arrive already trained on your EMR, PPE policy and floor plan." },
      { title: "Compliance evidence bundled", body: "Immunisation, vulnerable-sector, and CPR — audit-ready per shift." },
    ],
    how_it_works: [
      "Facility MSA setup with our staffing director.",
      "Roster of matched PSWs and RPNs pre-oriented.",
      "24/7 shift-request portal and phone line.",
      "Monthly compliance and utilisation report.",
    ],
    faq: [
      { q: "Do you cover statutory holidays?", a: "Yes — our on-call bench is doubled on statutory holidays and long weekends." },
    ],
    cta: "Speak with a staffing director",
  },
  {
    slug: "retirement-homes",
    title: "Retirement Home Staffing",
    tagline: "Elevated hospitality, backed by clinical rigour.",
    division: "staffing",
    cover: "https://images.unsplash.com/photo-1777269749032-d8d458ae594d",
    meta_description:
      "Xtreme Care Services provides retirement home staffing across Canada — from PSWs and cook helpers to registered nurses for assisted living wings.",
    keywords: ["retirement home care staff", "supplementary caregivers Canada", "assisted living staffing"],
    intro:
      "Retirement residents pay for a lifestyle, not a hospital. Our retirement-home roster is selected for warmth first, competence always — PSWs and RNs who understand this floor is somebody's home.",
    benefits: [
      { title: "Hospitality-forward selection", body: "We hire for warmth as aggressively as we hire for clinical skill." },
      { title: "Assisted-living scale", body: "Support for independent, assisted and memory-care wings under one contract." },
      { title: "Programming support", body: "Our activation aides fill in on programming, not just personal care." },
    ],
    how_it_works: [
      "Facility tour and needs mapping.",
      "Roster of retirement-experienced PSWs and RNs.",
      "Wing-specific rotation to preserve resident continuity.",
      "Quarterly resident-experience report.",
    ],
    faq: [
      { q: "Can you staff dining and activation roles too?", a: "Yes — our roster includes activation aides, dining assistants and cook helpers." },
    ],
    cta: "Request a retirement staffing plan",
  },
  {
    slug: "group-homes",
    title: "Group Home Staffing",
    tagline: "Specialised support workers for specialised environments.",
    division: "staffing",
    cover: "https://images.unsplash.com/photo-1777269749032-d8d458ae594d",
    meta_description:
      "DSWs and behaviour-trained PSWs supplied by Xtreme Care Services to Canadian group homes, transitional housing and specialised residential settings.",
    keywords: ["group home support workers", "specialized facility staffing Canada", "developmental support workers"],
    intro:
      "Group homes need staff who understand behaviour plans, who can read escalation cues, who can hold boundaries with warmth. Our developmental service workers arrive already fluent in CPI, ABA and person-directed planning.",
    benefits: [
      { title: "Behaviour plan literacy", body: "DSWs oriented on individual behaviour support plans before shift one." },
      { title: "Trauma-informed", body: "Every worker trained in trauma-informed care fundamentals." },
      { title: "Documentation ready", body: "ABC data collection and shift notes formatted to your provider's requirements." },
    ],
    how_it_works: [
      "Home-by-home behavioural walkthrough.",
      "Matched DSW roster with backup layer.",
      "Bi-weekly clinical supervision touchpoints.",
    ],
    faq: [
      { q: "Do you cover community outings?", a: "Yes — 1:1 and 2:1 community access is a common request." },
    ],
    cta: "Discuss group home staffing",
  },
  {
    slug: "private-nursing",
    title: "Private Nursing (Licensed Nurses)",
    tagline: "Registered clinical care, delivered privately.",
    division: "home-care",
    cover: "https://images.pexels.com/photos/7551617/pexels-photo-7551617.jpeg",
    meta_description:
      "Private-duty registered nurses and RPNs from Xtreme Care Services. Wound care, IV therapy, catheter management, medication administration — nationwide in Canada.",
    keywords: ["private licensed nurses Canada", "in-home nursing services", "registered nurse home care"],
    intro:
      "There are moments in a recovery when only a licensed nurse will do. Our private nursing division deploys RNs and RPNs to homes, hotel suites and executive residences for wound care, IV therapy, medication administration and clinical oversight.",
    benefits: [
      { title: "Scope-of-practice clarity", body: "You get the right credential for the right task — never over- or under-scoped." },
      { title: "Concierge scheduling", body: "Nurses arrive within named 30-minute windows." },
      { title: "Physician liaison", body: "Nurses coordinate directly with your treating physician." },
    ],
    how_it_works: [
      "Clinical intake with our director of nursing.",
      "Care plan authored in tandem with your physician.",
      "Nurse deployed on a repeating window.",
      "Weekly clinical summary to family and physician.",
    ],
    faq: [
      { q: "Can nurses travel with clients?", a: "Yes — we routinely dispatch nurses to hotels, second homes and travel itineraries." },
    ],
    cta: "Engage a private nurse",
  },
];

export const PROVINCES = [
  { code: "ON", name: "Ontario", cities: ["Toronto", "Ottawa", "Mississauga", "Hamilton", "London", "Brampton", "Kitchener", "Windsor", "Thunder Bay", "Sudbury", "Kingston", "Barrie", "Guelph", "Oshawa", "St. Catharines", "Waterloo", "Cambridge", "Peterborough", "Sault Ste. Marie", "North Bay", "Ajax", "Aurora", "Belleville", "Brantford", "Burlington", "Chatham-Kent", "Clarington", "Collingwood", "Cornwall", "Fort Erie", "Halton Hills", "Innisfil", "Markham", "Milton", "Newmarket", "Niagara Falls", "Orangeville", "Orillia", "Pickering", "Quinte West", "Richmond Hill", "Sarnia", "Stratford", "Timmins", "Vaughan", "Welland", "Whitby", "Woodstock", "Bracebridge", "Huntsville", "Owen Sound", "Cobourg", "Georgina"] },
  { code: "QC", name: "Quebec", cities: ["Montréal", "Québec City", "Laval", "Gatineau", "Longueuil", "Sherbrooke", "Saguenay", "Lévis", "Trois-Rivières", "Terrebonne", "Blainville", "Brossard", "Drummondville", "Granby", "Joliette", "Rimouski", "Rouyn-Noranda", "Saint-Jean-sur-Richelieu", "Saint-Jérôme", "Sept-Îles", "Shawinigan", "Val-d'Or", "Repentigny", "Mirabel", "Boucherville", "Victoriaville", "Salaberry-de-Valleyfield", "Sorel-Tracy"] },
  { code: "BC", name: "British Columbia", cities: ["Vancouver", "Victoria", "Surrey", "Burnaby", "Richmond", "Kelowna", "Abbotsford", "Coquitlam", "Kamloops", "Nanaimo", "Prince George", "Chilliwack", "Campbell River", "Courtenay", "Cranbrook", "Dawson Creek", "Delta", "Duncan", "Fort St. John", "Langley", "Maple Ridge", "Mission", "New Westminster", "North Vancouver", "Penticton", "Port Coquitlam", "Vernon", "White Rock", "Squamish", "Salmon Arm", "Comox", "Prince Rupert", "Terrace", "Powell River"] },
  { code: "AB", name: "Alberta", cities: ["Calgary", "Edmonton", "Red Deer", "Lethbridge", "Medicine Hat", "Fort McMurray", "Grande Prairie", "St. Albert", "Airdrie", "Camrose", "Cochrane", "Leduc", "Lloydminster", "Okotoks", "Spruce Grove", "Wetaskiwin", "Beaumont", "Chestermere", "High River", "Fort Saskatchewan", "Sylvan Lake", "Canmore", "Brooks", "Stony Plain"] },
  { code: "MB", name: "Manitoba", cities: ["Winnipeg", "Brandon", "Steinbach", "Thompson", "Portage la Prairie", "Selkirk", "Winkler", "Morden", "Dauphin", "The Pas", "Flin Flon", "Neepawa", "Stonewall"] },
  { code: "SK", name: "Saskatchewan", cities: ["Saskatoon", "Regina", "Prince Albert", "Moose Jaw", "Swift Current", "Yorkton", "North Battleford", "Estevan", "Warman", "Weyburn", "Melfort", "Humboldt", "Martensville", "Meadow Lake"] },
  { code: "NS", name: "Nova Scotia", cities: ["Halifax", "Sydney", "Dartmouth", "Truro", "New Glasgow", "Amherst", "Antigonish", "Bridgewater", "Kentville", "Yarmouth", "Glace Bay", "Wolfville", "Digby", "Pictou"] },
  { code: "NB", name: "New Brunswick", cities: ["Moncton", "Saint John", "Fredericton", "Dieppe", "Bathurst", "Miramichi", "Edmundston", "Campbellton", "Oromocto", "Riverview", "Quispamsis", "Rothesay", "Sackville", "Shediac"] },
  { code: "NL", name: "Newfoundland & Labrador", cities: ["St. John's", "Mount Pearl", "Corner Brook", "Grand Falls-Windsor", "Gander", "Paradise", "Conception Bay South", "Happy Valley-Goose Bay", "Labrador City", "Torbay", "Clarenville", "Stephenville", "Marystown", "Bay Roberts"] },
  { code: "PE", name: "Prince Edward Island", cities: ["Charlottetown", "Summerside", "Stratford", "Cornwall", "Montague", "Kensington", "Souris", "Alberton", "Georgetown"] },
  { code: "YT", name: "Yukon", cities: ["Whitehorse", "Dawson City", "Watson Lake", "Haines Junction", "Carmacks", "Faro", "Mayo", "Teslin"] },
  { code: "NT", name: "Northwest Territories", cities: ["Yellowknife", "Hay River", "Inuvik", "Fort Smith", "Norman Wells", "Fort Simpson", "Behchokǫ̀", "Tuktoyaktuk"] },
  { code: "NU", name: "Nunavut", cities: ["Iqaluit", "Rankin Inlet", "Cambridge Bay", "Arviat", "Baker Lake", "Pond Inlet", "Igloolik", "Kugluktuk"] },
];

export const BLOG_POSTS = [
  {
    slug: "aging-in-place-canada-2026",
    title: "Aging in Place in Canada: What the 2026 Landscape Actually Looks Like",
    excerpt: "Home is where most Canadians want to grow old — but the infrastructure lags. Here is what families need to know this year.",
    cover: "https://images.unsplash.com/photo-1765896387387-0538bc9f997e",
    category: "Family Guide",
    date: "2026-01-08",
    read_time: "9 min",
    author: "Xtreme Care Editorial",
    content: [
      { h2: "The quiet shift", p: "For three consecutive years the federal aging-in-place figures have moved in the same direction: more Canadian seniors, more of them living independently, and fewer of them choosing residential facilities as a first option. What has not kept pace is the community-level infrastructure to support that decision." },
      { h2: "Where the gaps sit", p: "Home care hours funded through provincial programs vary by nearly 400% between provinces. Families in Ontario, for example, are eligible for a very different quantum of subsidised hours than families in Alberta or the Atlantic provinces. Understanding your provincial allotment is the first step in any real home-care plan." },
      { h2: "The private layer", p: "For most families, provincial coverage is a foundation, not a ceiling. A private layer — the sort of consistent, rotating PSW care Xtreme Care provides — is what keeps a household running when subsidised hours end at 3pm." },
      { h2: "What we recommend this year", p: "Start early. Assemble the paperwork before the crisis. Interview your caregiver before you need one. Aging in place is not a decision made in a hospital corridor — it is a plan made at a kitchen table, months in advance." },
    ],
  },
  {
    slug: "dementia-sundowning-guide",
    title: "Sundowning: A Practical Guide for Canadian Families",
    excerpt: "When late afternoon becomes the hardest part of the day, structure is medicine. A room-by-room protocol.",
    cover: "https://images.pexels.com/photos/5790833/pexels-photo-5790833.jpeg",
    category: "Dementia Care",
    date: "2025-12-19",
    read_time: "7 min",
    author: "Priya M., RN",
    content: [
      { h2: "What sundowning is — and what it isn't", p: "Sundowning is the collection of behavioural and cognitive changes that appear in some people with dementia as afternoon becomes evening. It is not a stage of the disease. It is not a behaviour problem. It is, most usefully, an environmental phenomenon we can influence." },
      { h2: "The lighting rule", p: "Turn every light on thirty minutes before you would naturally reach for the switch. Contrast, not ambience, is the goal." },
      { h2: "The 4pm reset", p: "A short walk, a warm drink, a familiar piece of music. Ritualise the transition into evening and the transition softens." },
      { h2: "When to call in support", p: "If sundowning is producing safety incidents — wandering, falls, aggression — the household needs a specialised memory-care caregiver, at least through the evening window." },
    ],
  },
  {
    slug: "hospital-discharge-checklist",
    title: "The 72-Hour Hospital Discharge Checklist Every Family Should Print",
    excerpt: "A one-page protocol from our RN team — everything to sort before your loved one leaves the ward.",
    cover: "https://images.unsplash.com/photo-1584451049700-ec9b394f3805",
    category: "Recovery",
    date: "2025-12-04",
    read_time: "6 min",
    author: "Marcus O., RPN",
    content: [
      { h2: "Before you leave the room", p: "Ask three questions: what medications changed, what warning signs matter, and what follow-up appointments are already booked." },
      { h2: "The medication reconciliation moment", p: "Bring the pre-admission medication list to discharge. Do not leave until a nurse has confirmed which of those are stopped, changed, or continued." },
      { h2: "The home audit", p: "Loose rugs, low toilets, dim hallways. The first 72 hours home are not the time for a beautiful home — they are the time for a safe one." },
      { h2: "Where private support fits", p: "If the client lives alone or the primary caregiver still works, a dedicated PSW for the first three days is the single highest-value private-pay decision a family makes all year." },
    ],
  },
  {
    slug: "psw-career-canada",
    title: "So You Want to Be a PSW in Canada: A Career Field Guide",
    excerpt: "What the credential actually opens, what it pays, and how to build a career instead of a job.",
    cover: "https://images.pexels.com/photos/6129496/pexels-photo-6129496.jpeg",
    category: "Careers",
    date: "2025-11-22",
    read_time: "10 min",
    author: "Xtreme Care Talent",
    content: [
      { h2: "The credential", p: "In most provinces, a Personal Support Worker certificate is delivered through a college or private career college program of roughly 700 hours. That is a starting line, not a finish line." },
      { h2: "The specialisation ladder", p: "PSWs who complete supplementary certificates — Gentle Persuasive Approach, palliative fundamentals, CPI — move faster into higher-paying assignments and stable rotations." },
      { h2: "Agency vs. facility", p: "Agency work rewards flexibility and pays a premium; facility work rewards routine. The best careers, in our experience, blend both intentionally." },
      { h2: "Working with us", p: "Xtreme Care is hiring PSWs in every province. We pay premium hourly rates, cover credential renewals, and build predictable rotations. Apply through our Careers page." },
    ],
  },
  {
    slug: "palliative-conversations-canada",
    title: "The Conversation No Family Wants to Rush: A Framework for Palliative Discussions",
    excerpt: "Language that opens the palliative conversation without closing the person.",
    cover: "https://images.pexels.com/photos/6975192/pexels-photo-6975192.jpeg",
    category: "Palliative",
    date: "2025-10-30",
    read_time: "8 min",
    author: "Dr. Anaya Weber (guest)",
    content: [
      { h2: "Why the words matter", p: "The move from curative to comfort-focused care is not merely a medical decision — it is a linguistic one. The language a family uses in the days before that transition shapes the years after it." },
      { h2: "Three sentences that help", p: "'We want as much time as possible, and we also want that time to feel like you.' 'What would a good day look like right now?' 'Who do we need in the room?'" },
      { h2: "What a palliative caregiver actually does", p: "Presence. Symptom vigilance. Small, unhurried, competent care that lets the family be the family, not the shift." },
      { h2: "How to begin", p: "A single call to our palliative coordinator opens the door. We meet you where you are." },
    ],
  },
];
